import { EcgsApiService } from "../api/EcgsService";
import { EcgData, EcgLeads } from "../api/index.defs";
import { ProfilesApiService } from "../api/ProfilesService";
import { UsersApiService } from "../api/UsersService";
import { Constants } from "../constants";
import { IBaseEcg } from "../models/base-ecg.interface";
import {
  IEcg,
  IEcgDetails,
  IEcgsFilter,
  Note,
  UpdateEcg,
} from "../models/ecg.interface";
import { CancelableRequest } from "../models/request.interface";
import { IResult } from "../models/results/result.interface";
import {
  ISaveInterpretedEcg,
  SaveInterpretedEcgFailureReason,
} from "../models/results/save-interpreted-ecg.interface";
import { getSaveInterpretedEcgFailure, mapToEcgNotes } from "../utils/ecgUtil";
import { getApiBasePath } from "../utils/envUtil";
import { b64ToBlob } from "../utils/fileUtil";
import LocalStorageUtil from "../utils/localStorage";
import logger from "../utils/logger";
import {
  getFullNameWithTitle,
  getGenderFromServerGender,
} from "../utils/userUtil";

const mapToEcgDetails = (
  id: string,
  data: EcgData,
  isCompare?: boolean,
  ecgGraphImages?: EcgLeads
): IEcgDetails => {
  const {
    actions,
    actionsReviewNote,
    comments,
    electrodesStatus,
    findings,
    interpretation,
    measurements,
    permissions,
    symptoms,
    isPriority,
    remarks,
    deviceSN,
    reference,
    approve,
    timeout,
    hasPacemaker,
    isUnreadableBySystem,
    ecgReviewState
  } = data;

  const ecgDetails: IEcgDetails = {
    id: id,
    comments: remarks,
    notes: mapToEcgNotes(comments),
    electrodesStatus: electrodesStatus,
    measurements: measurements,
    symptoms: symptoms,
    date: "",
    isUrgent: isPriority?.value,
    reference: reference,
    saveOnce: permissions?.saveOnce,
    showSuggestive: permissions?.showSuggestiveOf,
    deviceSerialNumber: deviceSN,
    timeout,
    approval: !approve
      ? undefined
      : {
        date: approve.edit.date ?? "",
        user: {
          id: approve.edit.userId ?? "",
          display: getFullNameWithTitle(
            approve.edit.salutation,
            approve.edit.firstName,
            approve.edit.lastName
          ),
        },
      },
    hasPacemaker,
    isUnreadableBySystem,
    ecgReviewState
  };

  const isUnreadable = findings?.some(
    (f) =>
      Constants.ECG_UNREADABLE_FINDINGS_CODE_1 === f.code.toString() && f.value
  );

  if (!isCompare) {
    ecgDetails.permissions = permissions;

    ecgDetails.graph = ecgGraphImages;
    ecgDetails.isReadable = !isUnreadable;

    if (interpretation) {
      ecgDetails.interpretation = {
        text: interpretation.text ?? "",
        date: interpretation.edit?.date ?? "",
        user: {
          id: interpretation.edit?.userId ?? "",
          display: getFullNameWithTitle(
            interpretation.edit?.salutation,
            interpretation.edit?.firstName,
            interpretation.edit?.lastName
          ),
        },
      };
    }

    ecgDetails.actions =
      actions?.map((a) => ({
        key: a.code.toString(),
        value: a.value !== false,
        displayName: a.name,
        severity: a.severity,
      })) ?? [];
    ecgDetails.actionsReviewNote = actionsReviewNote;

    ecgDetails.findings =
      findings?.map((f) => {
        const useLeads = !!ecgDetails.permissions?.enableLeads.find(
          (lead) => lead.findingCode === f.code
        )?.useLeads;

        return {
          key: f.code.toString(),
          value: f.value !== false,
          displayName: f.name,
          suggestiveOf: f.category,
          useLeads,
          isEditable: f.isEditable,
          leads: f.leads?.map((l) => ({
            code: l.code,
            value: l.value !== false,
            name: l.name,
            //TODO: remove this hardcode once the disabled flag will be retrieved by the API
            disabled:
              f.code === Constants.ECG_INVERTED_T_WAVES_FINDING_CODE &&
              l.code === Constants.ECG_AVR_LEAD_CODE,
          })),
        };
      }) ?? [];
  }

  return ecgDetails;
};

const getEcgsByPatientId = async (id: string): Promise<IBaseEcg[] | null> => {
  logger.info(getEcgsByPatientId.name, `Getting patient ${id} ecgs `);

  try {
    const {
      response: resp,
      error,
    } = await ProfilesApiService.profilesGetProfileEcgs({
      profileGuid: id,
    });

    if (error || !resp) {
      logger.error(getEcgsByPatientId.name, "Invalid API response", {
        resp,
        error,
      });
      return null;
    }

    const ecgs: IBaseEcg[] = resp.map((ecg) => {
      const baseEcg: IBaseEcg = {
        id: ecg.ecgId!,
        date: ecg.ecgDateTime!,
        isUrgent: ecg.isPriority,
        unviewedEcg: ecg.unviewedInPortal,
        deviceSerialNumber: ecg.deviceSn,
        reference: ecg.reference,
      };
      return baseEcg;
    });

    return ecgs;
  } catch (error) {
    logger.error(
      getEcgsByPatientId.name,
      "Error Getting ecgs for patient.",
      error
    );
    return null;
  }
};

const getPatientComparableEcgs = async ({
  patientId,
  filterIds = [],
  cancelToken,
}: CancelableRequest<{ patientId: string; filterIds: string[] }>) => {
  logger.info(
    getPatientComparableEcgs.name,
    `Getting patient ${patientId} comparable ecgs..`
  );

  try {
    const {
      response: resp,
      error,
    } = await ProfilesApiService.profilesGetEcgHeaders(
      {
        profileGuid: patientId,
      },
      { cancelToken }
    );

    if (error || !resp) {
      logger.error(getPatientComparableEcgs.name, "Invalid API response", {
        resp,
        error,
      });
      return null;
    }

    return resp
      .filter((ecg) => !filterIds.includes(ecg.ecgId))
      .map((ecg) => ({
        id: ecg.ecgId,
        date: ecg.ecgDateTime,
        hasSymptoms: ecg.hasSymptoms,
        isUnreadable: ecg.isUnreadable
      }));
  } catch (error) {
    logger.error(
      getPatientComparableEcgs.name,
      "Error Getting patient comparable ecgs.",
      error
    );
    return null;
  }
};

export type GetEcgsByUserIdProps = {
  userId: string;
  pageIndex: number;
  pageSize: number;
  filters?: IEcgsFilter;
  pageAmount?: number;
};

const getEcgsByUserId = async ({
  userId,
  pageIndex,
  pageSize,
  pageAmount,
  filters,
}: GetEcgsByUserIdProps): Promise<IResult<{
  ecgs: IEcg[];
  total: number;
  actualCount?: number;
}>> => {
  logger.info(getEcgsByUserId.name, `Getting ecgs by user id ${userId}`);

  try {
    const { response, error } = await UsersApiService.usersGetPortalEcgList({
      userId,
      page: pageIndex,
      pageSize,
      pageAmount,
      ...filters,
    });

    if (error || !response) {
      logger.error(getEcgsByUserId.name, "Invalid API response", {
        response,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    const { ecgs, totalCount, actualCount } = response;

    return {
      success: true,
      data: {
        actualCount: actualCount || 0,
        total: totalCount ?? 0,
        ecgs: !ecgs
          ? []
          : ecgs.map(
            (apiEcg): IEcg => ({
              id: apiEcg.ecgId,
              date: apiEcg.ecgDateTime,
              unviewedEcg: apiEcg.unviewedInPortal,
              deviceSerialNumber: apiEcg.deviceSN,
              isUrgent: apiEcg.isPriority,
              organization: {
                id: apiEcg.organization?.organizationId?.toString() ?? "",
                rootName: apiEcg.organization?.rootOrganizationName ?? "",
                name: apiEcg.organization?.organizationName ?? "",
                phone: apiEcg.organization?.phone,
                email: apiEcg.organization?.email,
              },
              patient: {
                id: apiEcg.profile.id,
                firstName: apiEcg.profile.firstName,
                lastName: apiEcg.profile.lastName,
                dateOfBirth: apiEcg.profile.dateOfBirth,
                gender: getGenderFromServerGender(apiEcg.profile.gender),
                email: apiEcg.profile?.email ?? "",
                phone: apiEcg.profile?.phoneNumber ?? "",
              },
              reference: apiEcg.profile.reference,
              minutesRemaining: apiEcg.minutesRemaining,
              isReviewed: apiEcg.isSaved,
              hasFindings: apiEcg.hasFindings,
              hasInterpretations: apiEcg.hasInterpretation,
              isApproved: apiEcg.isApproved,
              reviewType: apiEcg.reviewType,
              isSaved: apiEcg.isSaved,
              isArchived: apiEcg.isArchived,
              state: apiEcg.usState,
              isUnreadable: apiEcg.isUnreadable,
              actionSeverity: apiEcg.actionSeverity,
            })
          )
      },
    };
  } catch (error) {
    logger.error(getEcgsByUserId.name, "Error Getting ecgs by user id.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const getEcgDetails = async ({
  id,
  compareId,
  cancelToken,
}: CancelableRequest<{
  id: string;
  compareId?: string;
}>): Promise<IResult<{
  ecgDetails: IEcgDetails;
  compareEcgDetails: IEcgDetails | undefined;
}>> => {
  logger.info(
    getEcgDetails.name,
    `Getting ecg ${id} details`,
    `Compare: ${compareId}`
  );

  try {
    const { response: resp, error } = await EcgsApiService.ecgsGetEcgData(
      {
        ecgId: id,
        compareTo: compareId,
      },
      { cancelToken }
    );

    if (error || !resp || !resp.ecgs?.length) {
      logger.error(getEcgDetails.name, "Invalid API response", { resp, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }
    const ecgGraphImages = resp.leads;

    const ecgDetails = mapToEcgDetails(id, resp.ecgs[0], false, ecgGraphImages);

    let compareEcgDetails: IEcgDetails | undefined = undefined;
    if (compareId) {
      compareEcgDetails = mapToEcgDetails(compareId, resp.ecgs[1]);
    }

    return {
      success: true,
      data: { ecgDetails, compareEcgDetails },
    };
  } catch (error) {
    logger.error(getEcgDetails.name, "Error getting ecg details.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const requestEcrOnDemand = async (ecgId: string): Promise<IResult> => {
  logger.info(requestEcrOnDemand.name, `Trying to request ECR on demand...`);
  try {
    const { response: resp, error } = await EcgsApiService.ecgsRequestEcrOnDemand({ ecgId, })

    if (error || !resp) {
      logger.error(requestEcrOnDemand.name, "Invalid API response", { resp, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return { success: true };

  } catch (e) {
    logger.error(requestEcrOnDemand.name, "Error requesting ecr on demand.", e);
    return { success: false, message: (e as Error).message };
  }
}

const updateEcg = async (ecg: UpdateEcg): Promise<ISaveInterpretedEcg> => {
  logger.info(updateEcg.name, `Trying to update ECG...`, ecg);
  try {
    const { id, actions, findings, interpretation, isUrgent } = ecg;
    const { response: resp, error } = await EcgsApiService.ecgsSaveEcg({
      ecgId: id,
      draftData: {
        interpretation: {
          text: interpretation ?? "",
        },
        findings:
          findings?.map((f) => ({
            code: +f.key,
            value: f.value ?? false,
            leads: f.useLeads
              ? f.leads?.map((l) => ({
                code: l.code,
                value: l.value,
                name: l.name,
              }))
              : undefined,
          })) ?? [],
        actions:
          actions?.map((a) => ({
            code: +a.key,
            value: a.value ?? false,
          })) ?? [],
        isPriority: { value: isUrgent ?? false },
      },
    });

    if (error || !resp) {
      logger.error(updateEcg.name, "Invalid API response", { resp, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
        failReason: getSaveInterpretedEcgFailure(
          error?.errorCode?.toString() ?? ""
        ),
      };
    }

    // Ecg updated successfully
    return {
      success: true,
      postSaveMessageCode: resp.postSaveMessageCode,
    };
  } catch (error) {
    logger.error(updateEcg.name, "Error updating ecg.", error);
    return {
      success: false,
      message: (error as Error).message,
      failReason: SaveInterpretedEcgFailureReason.Unknown,
    };
  }
};

const downloadPdf = async (ecgId: string): Promise<IResult<Blob>> => {
  logger.info(downloadPdf.name, `Getting ecg ${ecgId} PDF..`);

  try {
    const { response: resp, error } = await EcgsApiService.ecgsGetEcgPdf({
      ecgId: ecgId,
    });

    if (error || !resp || !resp.ecgPdfBytes) {
      logger.error(downloadPdf.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    const file = b64ToBlob(resp.ecgPdfBytes, "application/pdf");

    return {
      success: true,
      data: file,
    };
  } catch (error) {
    logger.error(downloadPdf.name, "Error Getting ecg's PDF.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

export type GetPdfByTokenParams = {
  pdfToken: string;
  authToken?: string;
  ssoToken?: string;
  userName?: string;
  password?: string;
};

const downloadPdfByToken = async ({
  pdfToken,
  authToken,
  ssoToken,
  userName,
  password,
}: GetPdfByTokenParams): Promise<IResult<Blob>> => {
  logger.info(downloadPdfByToken.name, "Getting PDF by token");

  try {
    const { response: resp, error } = await EcgsApiService.ecgsGetEcgPdfByToken(
      {
        ecgDto: { pdfToken, ssoToken, authToken, userName, password },
      }
    );

    if (error || !resp || !resp.ecgPdfBytes) {
      logger.error(downloadPdfByToken.name, "Invalid API response", {
        resp,
        error,
      });

      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    const file = b64ToBlob(resp.ecgPdfBytes, "application/pdf");

    return {
      success: true,
      data: file,
    };
  } catch (error) {
    logger.error(downloadPdfByToken.name, "Error Getting ecg's PDF.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const approveEcg = async (
  ecgId: string
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(approveEcg.name, `Trying to approve ECG ${ecgId}`);
  try {
    const { response: resp, error } = await EcgsApiService.ecgsApproveEcg({
      ecgId: ecgId,
    });

    if (error || !resp) {
      logger.error(approveEcg.name, "Invalid API response", { resp, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    // Ecg approved successfully
    return {
      success: true,
    };
  } catch (error) {
    logger.error(approveEcg.name, "Error approving ecg.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const lockEcg = async (ecgId: string): Promise<IResult<number>> => {
  logger.info(lockEcg.name, `Trying to lock ECG ${ecgId}`);

  try {
    const {
      response: newTimeout,
      error,
    } = await EcgsApiService.ecgsExtendReviewSession({
      ecgId: ecgId,
    });

    if (error || !newTimeout) {
      logger.error(lockEcg.name, "Invalid API response", { newTimeout, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    // Ecg locked successfully
    return {
      success: true,
      data: newTimeout,
    };
  } catch (error) {
    logger.error(lockEcg.name, "Error locking ecg.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const unlockEcg = async (ecgId: string, token?: string): Promise<IResult> => {
  logger.info(unlockEcg.name, `Trying to unlock ECG ${ecgId}`);

  try {
    const { response: resp, error } = await EcgsApiService.ecgsEndReviewSession(
      {
        ecgId: ecgId,
        authorization: `Bearer ${token}`,
      }
    );

    if (error || !resp) {
      logger.error(unlockEcg.name, "Invalid API response", { resp, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    // Ecg locked successfully
    return {
      success: true,
    };
  } catch (error) {
    logger.error(unlockEcg.name, "Error unlocking ecg.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

export const releaseReviewLockedEcg = async () => {
  const reviewLockedEcgId = LocalStorageUtil.get(
    Constants.REVIEW_LOCKED_ECG_ID_KEY
  );
  const authToken = LocalStorageUtil.get(Constants.JWT_KEY);
  if (!reviewLockedEcgId || !authToken) return;

  /**
   * https://developer.mozilla.org/en-US/docs/Web/API/fetch
   * Using fetch API for the "keepAlive" option.
   * The keepalive option can be used to allow the request to outlive the page.
   * We must make sure that the unlock request is committed
   * and not canceled / aborted.
   * */
  const url = getApiBasePath() + `/ecgs/${reviewLockedEcgId}/review-sessions`;
  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    },
    keepalive: true, // keep-alive outlive the page
  };

  try {
    await fetch(url, requestOptions);
  } catch (error) {
    logger.error(releaseReviewLockedEcg.name, "Error releasing ecg.", error);
  }
  LocalStorageUtil.remove([Constants.REVIEW_LOCKED_ECG_ID_KEY]);
};

const addNote = async (ecgId: string, note: string): Promise<IResult> => {
  logger.info(
    addNote.name,
    `Trying to add note to ECG ${ecgId}. Note: ${note}`
  );

  try {
    const { response: resp, error } = await EcgsApiService.ecgsSaveEcgNotes({
      ecgId: ecgId,
      comment: {
        text: note,
      },
    });

    if (error || !resp) {
      logger.error(addNote.name, "Invalid API response", { resp, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    // Note added
    return {
      success: true,
    };
  } catch (error) {
    logger.error(addNote.name, "Error adding note to ECG.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const getNotes = async (ecgId: string): Promise<IResult<Note[]>> => {
  logger.info(addNote.name, `Trying to get notes of ECG ${ecgId}`);

  try {
    const { response: notesResp, error } = await EcgsApiService.ecgsGetEcgNotes(
      {
        ecgId: ecgId,
      }
    );

    if (error || !notesResp) {
      logger.error(addNote.name, "Invalid API response", { notesResp, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    const notes = mapToEcgNotes(notesResp);

    return {
      success: true,
      data: notes,
    };
  } catch (error) {
    logger.error(addNote.name, "Error adding note to ECG.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const addArchivingEcg = async (ecgId: string): Promise<IResult> => {
  logger.info(addArchivingEcg.name, `Archiving ecg ${ecgId}`);

  try {
    const { response: resp, error } = await EcgsApiService.ecgsArchiveEcg({
      ecgId: ecgId,
    });

    if (error || !resp) {
      logger.error(addArchivingEcg.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(addArchivingEcg.name, "Error archiving ecg.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const sendEcgByMail = async (
  ecgId: string,
  emails: string[]
): Promise<IResult> => {
  logger.info(sendEcgByMail.name, `Sending ecg ${ecgId}`);

  try {
    const { response: resp, error } = await EcgsApiService.ecgsSendEcgToEmails({
      ecgId,
      ecgByMailDto: { emails },
    });

    if (error || !resp) {
      logger.error(sendEcgByMail.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(sendEcgByMail.name, "Error sending ecg.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const ecgsService = {
  getEcgsByPatientId,
  getEcgsByUserId,
  getEcgDetails,
  updateEcg,
  downloadPdf,
  downloadPdfByToken,
  getPatientComparableEcgs,
  approveEcg,
  lockEcg,
  unlockEcg,
  releaseReviewLockedEcg,
  addNote,
  getNotes,
  addArchivingEcg,
  sendEcgByMail,
  requestEcrOnDemand
};

export default ecgsService;
